import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { mdiMagnify } from "@mdi/js";
import Icon from "@mdi/react";
import clsx from "clsx";
import dayjs from "dayjs";

import DataTable from "components/DataTable";
import Input from "components/Input";
import MultiSelect from "components/MultiSelect";
import penniesToDollars from "helpers/convertPenniesToDollars";
import useDebounce from "hooks/useDebounce";
import useInfiniteScroll from "hooks/useInfiniteScroll";
import { IOption } from "models/Form";
import { ITableHeaderList } from "models/HaulPay";
import {
  getOffPlatformReceivablesListAPI,
  OffPlatformReceivableResponse,
  OffPlatformReceivableStatus
} from "services/api/offPlatformReceivables";
import styles from "./style.module.scss";

export interface ReceivablesListFilters {
  search: string;
  statuses: OffPlatformReceivableStatus[];
}

interface IProps {
  onFiltersChange?: (filters: ReceivablesListFilters) => void;
}

const RECEIVABLE_STATUS_META: Record<
  OffPlatformReceivableStatus,
  { label: string; className: string }
> = {
  off_platform_open: { label: "Open", className: styles.statusOpen },
  off_platform_paid: { label: "Paid", className: styles.statusPaid },
  off_platform_settled: { label: "Settled", className: styles.statusSettled },
  off_platform_short_paid: { label: "Short paid", className: styles.statusShortPaid },
  off_platform_over_paid: { label: "Over paid", className: styles.statusOverPaid },
  off_platform_write_off: { label: "Write-off", className: styles.statusWriteOff }
};

const STATUS_ORDER: OffPlatformReceivableStatus[] = [
  "off_platform_open",
  "off_platform_paid",
  "off_platform_settled",
  "off_platform_short_paid",
  "off_platform_over_paid",
  "off_platform_write_off"
];

const PAGE_SIZE_OPTIONS = [20, 50, 100];
const DEFAULT_PAGE_SIZE = 20;
const SEARCH_DEBOUNCE_MS = 400;

const SORT_DISABLED_LIST = [
  "invoice_number",
  "load_number",
  "debtor_name",
  "carrier_name",
  "amount",
  "amount_balance",
  "status"
];

interface AgingCellValue {
  status: OffPlatformReceivableStatus;
  aging: number;
  paid_date: string | null;
}

const renderAgingCell = ({ status, aging, paid_date: paidDate }: AgingCellValue) => {
  if (status === "off_platform_write_off") {
    return "--";
  }
  if (status === "off_platform_paid" || status === "off_platform_over_paid") {
    return paidDate ? `Paid ${dayjs(paidDate).format("MMM D")}` : "--";
  }
  if (status === "off_platform_settled") {
    return paidDate ? `Settled ${dayjs(paidDate).format("MMM D")}` : "--";
  }
  return `${Math.max(aging ?? 0, 0)}d`;
};

const renderStatusCell = (status: OffPlatformReceivableStatus) => {
  const statusMeta = RECEIVABLE_STATUS_META[status];
  if (!statusMeta) {
    return "--";
  }
  return <span className={clsx(styles.statusBadge, statusMeta.className)}>{statusMeta.label}</span>;
};

const RECEIVABLES_HEADER_LIST: ITableHeaderList[] = [
  { label: "Invoice #", value: "invoice_number", render: (value: string) => value || "--" },
  { label: "Load #", value: "load_number", render: (value: string) => value || "--" },
  { label: "Debtor", value: "debtor_name", render: (value: string) => value || "--" },
  { label: "Carrier", value: "carrier_name", render: (value: string) => value || "--" },
  { label: "Amount", value: "amount", render: (value: number) => penniesToDollars(value ?? 0) },
  {
    label: "Balance",
    value: "amount_balance",
    render: (value: number) => penniesToDollars(value ?? 0)
  },
  { label: "Status", value: "status", render: renderStatusCell },
  { label: "Aging", value: ["status", "aging", "paid_date"], render: renderAgingCell }
];

const getErrorMessage = (error: unknown) => {
  const maybeError = error as { response?: { data?: { detail?: string } }; message?: string };
  return (
    maybeError?.response?.data?.detail ||
    maybeError?.message ||
    "Failed to fetch receivables. Please try again later."
  );
};

export default function ReceivablesList({ onFiltersChange }: Readonly<IProps>) {
  const [statusCounts, setStatusCounts] = useState<
    Partial<Record<OffPlatformReceivableStatus, number>>
  >({});
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedStatuses, setSelectedStatuses] = useState<OffPlatformReceivableStatus[]>([]);

  const debouncedSearch = useDebounce(searchTerm, SEARCH_DEBOUNCE_MS);
  const statusParam = selectedStatuses.join(",");

  const fetchReceivables = useCallback(
    async (params: { limit?: number; offset?: number }) => {
      const response = await getOffPlatformReceivablesListAPI({
        limit: params.limit,
        offset: params.offset,
        search: debouncedSearch.trim() || undefined,
        status: statusParam || undefined
      });
      setStatusCounts(response.data.status_counts || {});
      return response;
    },
    [debouncedSearch, statusParam]
  );

  const {
    data: rows,
    loading: isLoading,
    error,
    paginationInfo,
    goToPage,
    loadNextPage,
    loadPreviousPage,
    setPageSizeFn,
    resetPagination
  } = useInfiniteScroll<OffPlatformReceivableResponse>(
    fetchReceivables,
    { limit: DEFAULT_PAGE_SIZE },
    ["off-platform-receivables"]
  );
  const errorMessage = error ? getErrorMessage(error) : "";

  const isFirstFilterChangeRef = useRef(true);
  useEffect(() => {
    if (isFirstFilterChangeRef.current) {
      isFirstFilterChangeRef.current = false;
      return;
    }
    resetPagination();
  }, [debouncedSearch, statusParam, resetPagination]);

  useEffect(() => {
    onFiltersChange?.({ search: debouncedSearch.trim(), statuses: selectedStatuses });
  }, [debouncedSearch, selectedStatuses, onFiltersChange]);

  const totalStatusCount = useMemo(
    () => STATUS_ORDER.reduce((total, status) => total + (statusCounts[status] ?? 0), 0),
    [statusCounts]
  );

  const statusOptions: IOption[] = useMemo(
    () =>
      STATUS_ORDER.map((status, index) => ({
        id: index,
        value: status,
        label: `${RECEIVABLE_STATUS_META[status].label} (${statusCounts[status] ?? 0})`
      })),
    [statusCounts]
  );

  const selectedStatusOptions = useMemo(
    () =>
      statusOptions.filter((option) =>
        selectedStatuses.includes(option.value as OffPlatformReceivableStatus)
      ),
    [statusOptions, selectedStatuses]
  );

  return (
    <section className={styles.receivablesPanel}>
      <div className={styles.receivablesToolbar}>
        <Input
          className={styles.receivablesSearch}
          placeholder="Search by invoice number, load number, or debtor..."
          prefixNode={
            <Icon path={mdiMagnify} size={0.85} className={styles.receivablesSearchIcon} />
          }
          value={searchTerm}
          onChange={(event) => setSearchTerm(event.target.value)}
          isHideError
        />
        <MultiSelect
          className={styles.receivablesStatusFilter}
          options={statusOptions}
          value={selectedStatusOptions}
          onChange={(options) =>
            setSelectedStatuses(
              options.map((option) => option.value as OffPlatformReceivableStatus)
            )
          }
          placeholder={`Status: All (${totalStatusCount})`}
        />
      </div>
      {errorMessage ? <div className={styles.errorBanner}>{errorMessage}</div> : null}
      <DataTable
        key={`${debouncedSearch}-${statusParam}`}
        headers={RECEIVABLES_HEADER_LIST}
        data={rows}
        totalCount={paginationInfo.totalCount}
        nextLink={paginationInfo.hasNextPage}
        prevLink={paginationInfo.hasPreviousPage}
        loading={isLoading}
        fetchNextPage={loadNextPage}
        fetchPrevPage={loadPreviousPage}
        goToPage={goToPage}
        setPageSize={setPageSizeFn}
        itemPerPageOptions={PAGE_SIZE_OPTIONS}
        initialItemsPerPage={DEFAULT_PAGE_SIZE}
        sortDisableList={SORT_DISABLED_LIST}
      />
    </section>
  );
}
